import { isIterableArray } from '@/helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import ChildrensTooltip from './ChildrensTooltip';
import Flex from './Flex';

type AvatarSize = 's' | 'm' | 'l' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
type AvatarRounded = 'circle' | 'soft' | string;

export interface AvatarProps {
  size?: AvatarSize | string;
  rounded?: AvatarRounded;
  src?: string | string[];
  name?: ReactNode;
  tooltip?: string;
  emoji?: string;
  className?: string;
  mediaClass?: string;
  isExact?: boolean;
  icon?: IconProp;
}

export interface AvatarGroupProps {
  children?: ReactNode;
  dense?: boolean;
  className?: string;
}

const getInitials = (name: string) => name.match(/\b\w/g)?.join('') ?? name;

const Avatar = ({
  size = 'xl',
  rounded = 'circle',
  src,
  name,
  tooltip = '',
  emoji = '😊',
  className,
  mediaClass,
  isExact = false,
  icon
}: AvatarProps) => {
  const avatarClassName = classNames('avatar', `avatar-${size}`, className);
  const mediaClasses = classNames(
    rounded ? `rounded-${rounded}` : 'rounded',
    mediaClass
  );

  const getAvatar = () => {
    if (src) {
      if (isIterableArray(src)) {
        const [first, second, third] = src as string[];

        return (
          <div className={`${mediaClasses} overflow-hidden h-100 d-flex`}>
            <div className=" border-right">
              <img src={first} alt="" />
            </div>
            <div className="w-50 d-flex flex-column">
              <img src={second} alt="" className="h-50 border-bottom" />
              <img src={third} alt="" className="h-50" />
            </div>
          </div>
        );
      }

      return <img className={mediaClasses} src={src} alt="" />;
    }

    if (name) {
      return (
        <div className={`avatar-name ${mediaClasses}`}>
          <span>
            {isExact
              ? name
              : typeof name === 'string'
                ? getInitials(name)
                : name}
          </span>
        </div>
      );
    }

    if (icon) {
      return (
        <Flex className={`avatar-name ${mediaClasses}`}>
          <FontAwesomeIcon icon={icon} />
        </Flex>
      );
    }

    return (
      <div className={`avatar-emoji ${mediaClasses}`}>
        <span role="img" aria-label="Emoji">
          {emoji}
        </span>
      </div>
    );
  };

  return (
    <div dir="ltr" className={avatarClassName}>
      <ChildrensTooltip delayToRemove={0} placement="top" html={tooltip}>
        {getAvatar()}
      </ChildrensTooltip>
    </div>
  );
};

export const AvatarGroup = ({ children, dense, className }: AvatarGroupProps) => {
  return (
    <div
      className={classNames(className, 'avatar-group', {
        'avatar-group-dense': dense
      })}
    >
      {children}
    </div>
  );
};

export default Avatar;
